import CellTowerIcon from '@mui/icons-material/CellTower';
import ClassIcon from '@mui/icons-material/Class';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { alpha, Theme } from '@mui/material';
import { $T } from '../../../locale/types';
import {
  stationFilterOptionTypeLongMessages,
  stationFilterOptionTypeShortMessages,
  stationTypeMessages,
} from '../station-messages';
import { OptionType, StationFilterOption } from './types';
import { StationType } from '@app/core/types/stations';

/** Maps OptionType to the key used in station filter option type messages. */
const optionTypeToMessageKey: Record<StationFilterOption['type'], string> = {
  [OptionType.StationType]: 'stationType',
  [OptionType.StationGroup]: 'stationGroup',
  [OptionType.IndividualStation]: 'individualStation',
};

/** Returns the localized label for a station type (e.g. Independiente, Cabecera). */
export const getStationTypeLabel = ($t: $T, type: StationType): string =>
  $t(stationTypeMessages[type]);

// Helper function to generate ID from type and value
export const generateOptionId = (option: StationFilterOption): string => {
  switch (option.type) {
    case OptionType.StationType:
      return `${option.type}-${option.value}`;
    case OptionType.StationGroup:
      return `${option.type}-${option.value.id}`;
    case OptionType.IndividualStation:
      return `${option.type}-${option.value.id}`;
    default:
      return '';
  }
};

// Helper function to get icon for option type
export const getOptionIcon = (type: StationFilterOption['type']) => {
  switch (type) {
    case OptionType.StationType:
      return <ClassIcon fontSize="inherit" />;
    case OptionType.StationGroup:
      return <WorkspacesIcon fontSize="inherit" />;
    case OptionType.IndividualStation:
      return <CellTowerIcon fontSize="inherit" />;
    default:
      return null;
  }
};

const OPTION_TINT_ALPHA = 0.12;

/** Semantic main color for the given option type, if any. */
function getOptionMainColor(type: StationFilterOption['type'], theme: Theme): string | undefined {
  switch (type) {
    case OptionType.StationType:
      return theme.palette.info.main;
    case OptionType.StationGroup:
      return theme.palette.warning.main;
    case OptionType.IndividualStation:
      return theme.palette.success.main;
    default:
      return undefined;
  }
}

/** Soft semantic tint (transparent). Used for chips sitting on an opaque surface. */
function getOptionTintBackground(type: StationFilterOption['type'], theme: Theme): string {
  const main = getOptionMainColor(type, theme);
  if (!main) {
    return theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[100];
  }
  return alpha(main, OPTION_TINT_ALPHA);
}

/**
 * Opaque group-header background: mixes the semantic tint onto paper so sticky headers
 * do not let list options show through while scrolling.
 */
export const getOptionColor = (type: StationFilterOption['type'], theme: Theme): string => {
  const main = getOptionMainColor(type, theme);
  if (!main) {
    return theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[100];
  }
  return `color-mix(in srgb, ${main} ${OPTION_TINT_ALPHA * 100}%, ${theme.palette.background.paper})`;
};

/** Returns the localized group header for the given option type and variant. */
export const getGroupHeader = (
  $t: $T,
  type: StationFilterOption['type'],
  variant: 'short' | 'long',
): string => {
  const key = optionTypeToMessageKey[type];
  if (!key) return '';
  const messages =
    variant === 'long' ? stationFilterOptionTypeLongMessages : stationFilterOptionTypeShortMessages;
  return $t(messages[key]);
};

/** Light-mode chip tint — softer than palette.*.light, stronger than a bare wash. */
const LIGHT_CHIP_TINT_ALPHA = 0.1;

/** Light-mode chip background. */
function getLightChipBackground(type: StationFilterOption['type'], theme: Theme): string {
  switch (type) {
    case OptionType.StationType:
      return alpha(theme.palette.info.main, LIGHT_CHIP_TINT_ALPHA);
    case OptionType.StationGroup:
      return alpha(theme.palette.warning.main, LIGHT_CHIP_TINT_ALPHA);
    case OptionType.IndividualStation:
      return alpha(theme.palette.success.main, LIGHT_CHIP_TINT_ALPHA);
    default:
      return theme.palette.grey[100];
  }
}

function getChipBackgroundColor(type: StationFilterOption['type'], theme: Theme): string {
  if (theme.palette.mode === 'dark') {
    // Keep previous dark chip colors (same as group headers).
    return getOptionTintBackground(type, theme);
  }

  return getLightChipBackground(type, theme);
}

function getChipBorderColor(type: StationFilterOption['type'], theme: Theme): string {
  if (theme.palette.mode === 'dark') {
    return alpha(theme.palette.text.primary, 0.24);
  }

  switch (type) {
    case OptionType.StationType:
      return alpha(theme.palette.info.main, 0.22);
    case OptionType.StationGroup:
      return alpha(theme.palette.warning.main, 0.22);
    case OptionType.IndividualStation:
      return alpha(theme.palette.success.main, 0.22);
    default:
      return theme.palette.grey[300];
  }
}

/** Returns chip background and border colors for the given option type. */
export const getChipStyles = (type: StationFilterOption['type'], theme: Theme) => ({
  backgroundColor: getChipBackgroundColor(type, theme),
  borderColor: getChipBorderColor(type, theme),
});

// Helper function to sort options by type priority
export const sortOptionsByType = (options: StationFilterOption[]): StationFilterOption[] => {
  const typeOrder = [OptionType.StationType, OptionType.StationGroup, OptionType.IndividualStation];

  return [...options].sort((a, b) => {
    // First sort by type priority
    const aTypeIndex = typeOrder.indexOf(a.type);
    const bTypeIndex = typeOrder.indexOf(b.type);

    if (aTypeIndex !== bTypeIndex) {
      return aTypeIndex - bTypeIndex;
    }

    // Then sort alphabetically within each type
    return a.label.localeCompare(b.label);
  });
};
